import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { SidebarNavMode } from "../../Components/Sidebar/types";
import apiRequest, { get, put } from "../../Services/ApiRequest";
import { API_URLS } from "../../Utils/Constants/APIConstants";

interface UploadFileResponse {
  file_url?: string;
  file_name?: string;
  filename?: string;
  url?: string;
  file_size?: number;
  file_type?: string;
}

export interface Role {
  role_id: string;
  name: string;
}

/** Brief approver row from GET /api/v1/users/admin */
export interface AdminUser {
  user_id: string;
  full_name: string;
  email?: string;
  position?: string;
  role?: Role | string;
}

export interface DashboardCounts {
  total_executive_sponsors?: number;
  total_leaders_c_level?: number;
  total_org_admins?: number;
  total_accounts?: number;
  total_active_assignments?: number;
  assigned_accounts?: number;
  recent_assignments?: number;
  completed_assessments?: number;
}

interface MasterState {
  profilePictureUpload: {
    loading: boolean;
    error: string | null;
    response: UploadFileResponse | null;
  };
  userProfileImageUrl: {
    loading: boolean;
    error: string | null;
  };
  sidebarPreferences: {
    loading: boolean;
    error: string | null;
  };
  adminUsers: {
    items: AdminUser[];
    loading: boolean;
    error: string | null;
  };
  roles: {
    items: Role[];
    loading: boolean;
    error: string | null;
  };
  dashboardCounts: {
    data: DashboardCounts | null;
    loading: boolean;
    error: string | null;
  };
  loading: boolean;
  error: string | null;
}

const initialState: MasterState = {
  profilePictureUpload: {
    loading: false,
    error: null,
    response: null,
  },
  userProfileImageUrl: {
    loading: false,
    error: null,
  },
  sidebarPreferences: {
    loading: false,
    error: null,
  },
  adminUsers: {
    items: [],
    loading: false,
    error: null,
  },
  roles: {
    items: [],
    loading: false,
    error: null,
  },
  dashboardCounts: {
    data: null,
    loading: false,
    error: null,
  },
  loading: false,
  error: null,
};

const parseApiErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error && error.message) {
    try {
      const parsed = JSON.parse(error.message) as { message?: string };
      if (parsed?.message) return String(parsed.message);
    } catch {
      return error.message;
    }
  }
  return fallback;
};

export const uploadProfilePicture = createAsyncThunk(
  "master/uploadProfilePicture",
  async (file: File, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = (await apiRequest(
        "POST",
        API_URLS.uploadImage(),
        formData as any,
        false,
        true
      )) as UploadFileResponse;

      return response;
    } catch (error: unknown) {
      return rejectWithValue(
        parseApiErrorMessage(error, "Failed to upload profile picture")
      );
    }
  }
);

export const updateUserProfileImageUrl = createAsyncThunk(
  "master/updateUserProfileImageUrl",
  async (payload: { image_url: string }, { rejectWithValue }) => {
    try {
      const response = await put(API_URLS.userProfileImageUrl(), payload);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(
        parseApiErrorMessage(error, "Failed to update profile image URL")
      );
    }
  }
);

export const updateSidebarPreferences = createAsyncThunk(
  "master/updateSidebarPreferences",
  async (
    payload: {
      sidebar_nav_mode?: SidebarNavMode;
      sidebar_hover_enabled?: boolean;
      sidebar_pinned?: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await put(API_URLS.sidebarPreferences(), payload);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(
        parseApiErrorMessage(error, "Failed to update sidebar preference")
      );
    }
  }
);

export const fetchAdminUsers = createAsyncThunk(
  "master/fetchAdminUsers",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.getAdminUsers());
      return response as AdminUser[];
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch admin users";
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchRoles = createAsyncThunk(
  "master/fetchRoles",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.getRoles());
      const payload = response?.content ?? response;
      return (Array.isArray(payload) ? payload : []) as Role[];
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.response?.message ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch roles";
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchAdminRoles = createAsyncThunk(
  "master/fetchAdminRoles",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.getAdminRoles());
      const payload = response?.content ?? response;
      return (Array.isArray(payload) ? payload : []) as Role[];
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.response?.message ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch admin roles";
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchDashboardCounts = createAsyncThunk(
  "master/fetchDashboardCounts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.getDashboardCounts());
      return response as DashboardCounts;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch dashboard counts";
      return rejectWithValue(errorMessage);
    }
  }
);

const masterSlice = createSlice({
  name: "master",
  initialState,
  reducers: {
    clearProfilePictureSession: state => {
      state.profilePictureUpload.loading = false;
      state.profilePictureUpload.error = null;
      state.profilePictureUpload.response = null;
      state.userProfileImageUrl.loading = false;
      state.userProfileImageUrl.error = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(uploadProfilePicture.pending, state => {
        state.profilePictureUpload.loading = true;
        state.profilePictureUpload.error = null;
        state.profilePictureUpload.response = null;
      })
      .addCase(uploadProfilePicture.fulfilled, (state, action) => {
        state.profilePictureUpload.loading = false;
        state.profilePictureUpload.error = null;
        state.profilePictureUpload.response = action.payload;
      })
      .addCase(uploadProfilePicture.rejected, (state, action) => {
        state.profilePictureUpload.loading = false;
        state.profilePictureUpload.error = action.payload as string;
        state.profilePictureUpload.response = null;
      })
      .addCase(updateUserProfileImageUrl.pending, state => {
        state.userProfileImageUrl.loading = true;
        state.userProfileImageUrl.error = null;
      })
      .addCase(updateUserProfileImageUrl.fulfilled, state => {
        state.userProfileImageUrl.loading = false;
        state.userProfileImageUrl.error = null;
      })
      .addCase(updateUserProfileImageUrl.rejected, (state, action) => {
        state.userProfileImageUrl.loading = false;
        state.userProfileImageUrl.error = action.payload as string;
      })
      .addCase(updateSidebarPreferences.pending, state => {
        state.sidebarPreferences.loading = true;
        state.sidebarPreferences.error = null;
      })
      .addCase(updateSidebarPreferences.fulfilled, state => {
        state.sidebarPreferences.loading = false;
        state.sidebarPreferences.error = null;
      })
      .addCase(updateSidebarPreferences.rejected, (state, action) => {
        state.sidebarPreferences.loading = false;
        state.sidebarPreferences.error = action.payload as string;
      })
      .addCase(fetchAdminUsers.pending, state => {
        state.adminUsers.loading = true;
        state.adminUsers.error = null;
      })
      .addCase(fetchAdminUsers.fulfilled, (state, action) => {
        state.adminUsers.loading = false;
        state.adminUsers.items = action.payload;
        state.adminUsers.error = null;
      })
      .addCase(fetchAdminUsers.rejected, (state, action) => {
        state.adminUsers.loading = false;
        state.adminUsers.error = action.payload as string;
      })
      .addCase(fetchRoles.pending, state => {
        state.roles.loading = true;
        state.roles.error = null;
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.roles.loading = false;
        state.roles.items = action.payload;
        state.roles.error = null;
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        state.roles.loading = false;
        state.roles.error = action.payload as string;
      })
      .addCase(fetchAdminRoles.pending, state => {
        state.roles.loading = true;
        state.roles.error = null;
      })
      .addCase(fetchAdminRoles.fulfilled, (state, action) => {
        state.roles.loading = false;
        state.roles.items = action.payload;
        state.roles.error = null;
      })
      .addCase(fetchAdminRoles.rejected, (state, action) => {
        state.roles.loading = false;
        state.roles.error = action.payload as string;
      })
      .addCase(fetchDashboardCounts.pending, state => {
        state.dashboardCounts.loading = true;
        state.dashboardCounts.error = null;
      })
      .addCase(fetchDashboardCounts.fulfilled, (state, action) => {
        state.dashboardCounts.loading = false;
        state.dashboardCounts.data = action.payload;
        state.dashboardCounts.error = null;
      })
      .addCase(fetchDashboardCounts.rejected, (state, action) => {
        state.dashboardCounts.loading = false;
        state.dashboardCounts.error = action.payload as string;
      });
  },
});

export const { clearProfilePictureSession } = masterSlice.actions;

export default masterSlice.reducer;
